import {
  FoodPreferenceGroupV2,
  NormalizedNutritionV2,
  PreferenceItemV2,
} from "../type";

// Enum tables mirror the backend wire contract (v2 handoff, backend-reference/enums.json).
// Bit positions and enum values are append-only — safe to hard-code.

export const DIET_TYPE_LABELS: { [value: number]: string } = {
  0: "Non-Specific",
  1: "Mediterranean",
  2: "Vegetarian",
  3: "Vegan",
  4: "Paleo",
  5: "Keto",
  6: "Pescatarian",
  7: "Low-Carb",
  8: "Omnivore",
  9: "Lacto-ovo Vegetarian",
  10: "Lacto Vegetarian",
  11: "Ovo-vegetarian",
  12: "Energy Booster",
  13: "High-Protein",
  14: "Low-Cholesterol",
  15: "Cholesterol Free",
  16: "Low Sugar",
  17: "Sugar Free",
  18: "Halal",
  19: "Kosher",
  20: "Low Sodium",
  21: "Gluten Free",
  22: "Dairy Free",
  23: "Rich in Omega-3",
  24: "Sodium Free",
};

export const MEAL_TYPE_LABELS: { [value: number]: string } = {
  1: "Breakfast",
  2: "Morning Snack",
  3: "Morning Snack 2",
  4: "Lunch",
  5: "Afternoon Snack",
  6: "Dinner",
  7: "Dinner Snack",
  8: "Pre-workout Meal",
  9: "Intra-workout Meal",
  10: "Post-workout Meal",
};

// SD-6198 scale for coffee_frequency / alcohol_frequency (times per week).
export const FREQUENCY_LABELS: { [value: number]: string } = {
  0: "0",
  1: "1-2",
  2: "3-5",
  3: "6-7",
  4: "8-10",
  5: "11+",
};

// allergies bitmask → labels (backend-reference/ALLERGIES.md). Largest bit is 2^37.
export const DIETARY_RESTRICTIONS: [number, string][] = [
  [2, "Lactose intolerance"],
  [4, "Gluten intolerance"],
  [8, "Nut allergy"],
  [16, "Egg allergy"],
  [32, "Fish allergy"],
  [64, "Nightshades allergy"],
  [256, "Bloating"],
  [512, "Constipation"],
  [1024, "Diarrhea"],
  [2048, "Nausea"],
  [4096, "GERD"],
  [8192, "Cardiovascular disease"],
  [16384, "Diabetes"],
  [32768, "Metabolic syndrome"],
  [65536, "High blood pressure"],
  [131072, "Thyroid problems"],
  [262144, "Blood pressure management"],
  [524288, "Thyroid regulation"],
  [1048576, "Cholesterol"],
  [2097152, "Cardiovascular issues"],
  [4194304, "Insulin"],
  [8388608, "Glucose management"],
  [16777216, "Soy allergy"],
  [33554432, "Sesame allergy"],
  [67108864, "Shellfish allergy"],
  [134217728, "Hard-to-digest fruits"],
  [268435456, "Hard-to-digest vegetables"],
  [536870912, "Cashews"],
  [1073741824, "Pistachios"],
  [2147483648, "Fructose"],
  [4294967296, "Honey"],
  [8589934592, "Artificial sweeteners"],
  [17179869184, "Pork"],
  [34359738368, "Beef and its products"],
  [68719476736, "Mixing meat and dairy"],
  [137438953472, "Alcohol"],
];

// input_source values on food/recipe preference rows.
export const INPUT_SOURCE = {
  FRUITS: 2,
  VEGETABLES: 3,
  GRAINS: 4,
  PROTEIN_SOURCES: 5,
  FAT_SOURCES: 6,
  PRE_WORKOUT: 7,
  POST_WORKOUT: 8,
  ALCOHOL: 10,
  COFFEE: 11,
};

const EMPTY = "—";

// Bits above 2^31 (Fructose and up) get corrupted by JS 32-bit bitwise
// operators — test bits with integer division instead of `&`.
export const hasBit = (mask: number, bit: number) =>
  Math.floor(mask / bit) % 2 === 1;

export const decodeDietaryRestrictions = (mask: number): string[] =>
  DIETARY_RESTRICTIONS.filter(([bit]) => hasBit(mask, bit)).map(
    ([, label]) => label
  );

type RawFoodPreference = {
  food_name: string;
  preferred: boolean;
  input_source: number;
};

type RawFoodCategoryPreference = {
  category: number;
  category_name: string;
  // 0 = "Non-Specific" = the whole category was opted out.
  sub_category: number;
  sub_category_name: string;
  preferred: boolean;
  input_source: number;
};

const NON_SPECIFIC_SUB_CATEGORY = 0;

type RawRecipePreference = {
  recipe_name: string;
  preferred: boolean;
  input_source: number;
};

type RawSupplementPreference = { supplement_name: string };

type RawCheatFood = { food_name: string; weekdays: string[] };

export type RawNutritionQuestionnaireV2 = {
  diet_type?: number;
  daily_meal_types?: number[];
  biggest_meal_type?: number;
  waking_time?: string;
  lunch_time?: string;
  bed_time?: string;
  intermittent_fasting_start_time?: string;
  intermittent_fasting_end_time?: string;
  pre_workout_meal_type?: number;
  post_workout_meal_type?: number;
  coffee_frequency?: number;
  alcohol_frequency?: number;
  allergies?: number;
  food_preferences?: RawFoodPreference[];
  food_category_preferences?: RawFoodCategoryPreference[];
  preferred_key_ingredient_ids?: string[];
  recipe_preferences?: RawRecipePreference[];
  supplement_preferences?: RawSupplementPreference[];
  cheat_foods?: RawCheatFood[];
};

const mealLabel = (type: number | undefined) =>
  type !== undefined ? MEAL_TYPE_LABELS[type] ?? EMPTY : EMPTY;

const timeOrDash = (time: string | undefined) =>
  time && time !== "" ? time : EMPTY;

const frequencyLabel = (value: number | undefined) => {
  const label = FREQUENCY_LABELS[value ?? 0];
  return !label || label === "0" ? "No" : `${label}/Week`;
};

const bySource = (prefs: RawFoodPreference[], source: number) =>
  prefs.filter((item) => item.input_source === source);

// Popup lists show ♥ liked first, ✕ disliked below, opt-out rows last
// (stable within each band).
const itemRank = (item: PreferenceItemV2) =>
  item.optOut ? 2 : item.preferred ? 0 : 1;
const likedFirst = (items: PreferenceItemV2[]) =>
  [...items].sort((a, b) => itemRank(a) - itemRank(b));

const namesBySource = (prefs: RawFoodPreference[], source: number) =>
  bySource(prefs, source).map((item) => item.food_name);

const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

// Union of every cheat food's weekdays, in week order, abbreviated: "Thu, Fri"
const cheatDaysLabel = (cheatFoods: RawCheatFood[]) => {
  const days = new Set(
    cheatFoods.flatMap((food) => food.weekdays ?? []).map((d) => d.toLowerCase())
  );
  const ordered = WEEKDAY_ORDER.filter((day) => days.has(day)).map(
    (day) => day.charAt(0).toUpperCase() + day.slice(1, 3)
  );
  return ordered.length === 0 ? EMPTY : ordered.join(", ");
};

// The Food Preferences popup mirrors the questionnaire's 8 "master category"
// sections (mobile source of truth: PrecisionReactNative
// nutrition/model/foodTaxonomy.ts, MASTER_TO_CATEGORY_KEYS +
// MASTER_CATEGORY_DISPLAY). Each section combines:
//  - individual food picks from food_preferences, matched by input_source
//  - whole sub-category picks from food_category_preferences, matched by the
//    backend FoodCategory id (append-only wire contract, safe to hard-code)
// input_source 5 (protein) can't distinguish animal from plant, and dairy /
// starchy searches save as generic source 1, so those individual picks land in
// their own fallback groups below the masters they belong with.
const MASTER_FOOD_GROUPS: {
  label: string;
  sources: number[];
  categoryIds: number[];
}[] = [
  { label: "Fruits", sources: [INPUT_SOURCE.FRUITS], categoryIds: [10, 57] },
  {
    label: "Non-Starchy Vegetables",
    sources: [INPUT_SOURCE.VEGETABLES],
    categoryIds: [14],
  },
  {
    label: "Dairy & Alternatives",
    sources: [],
    categoryIds: [5, 13, 20, 40, 41, 66, 69, 72],
  },
  {
    label: "Grains",
    sources: [INPUT_SOURCE.GRAINS],
    categoryIds: [6, 11, 16, 17, 24, 31, 54, 61, 63, 64, 71, 87, 88, 89, 90],
  },
  { label: "Starchy Vegetables", sources: [], categoryIds: [1, 62] },
  {
    label: "Animal-Based Protein",
    sources: [],
    categoryIds: [
      21, 22, 27, 29, 45, 47, 48, 49, 50, 51, 52, 73, 74, 79, 80, 81, 82, 83,
    ],
  },
  { label: "Plant-Based Protein", sources: [], categoryIds: [3, 33, 60, 91] },
  {
    label: "Protein Sources",
    sources: [INPUT_SOURCE.PROTEIN_SOURCES],
    categoryIds: [],
  },
  {
    label: "Oils, Nuts & Healthy Fats",
    sources: [INPUT_SOURCE.FAT_SOURCES],
    categoryIds: [7, 19, 36, 39, 58, 92, 95],
  },
];

// Workout (7/8) and alcohol (10) sources stay out of this popup — they
// already have their own tiles.
const FOOD_PREFERENCES_EXCLUDED_SOURCES: number[] = [
  INPUT_SOURCE.PRE_WORKOUT,
  INPUT_SOURCE.POST_WORKOUT,
  INPUT_SOURCE.ALCOHOL,
];

const combinedFoodPreferenceGroups = (
  foodPrefs: RawFoodPreference[],
  categoryPrefs: RawFoodCategoryPreference[]
): FoodPreferenceGroupV2[] => {
  const usableCategoryPrefs = categoryPrefs.filter(
    (pref) =>
      pref.sub_category_name &&
      !FOOD_PREFERENCES_EXCLUDED_SOURCES.includes(pref.input_source)
  );
  const mappedCategoryIds = new Set(
    MASTER_FOOD_GROUPS.flatMap((group) => group.categoryIds)
  );

  // A sub_category of 0 ("Non-Specific") excludes a whole backend category.
  // The questionnaire's section-level opt-out ("I don't eat any …") writes one
  // such row for EVERY category of the section — but a section where the
  // customer picked specific items can still carry Non-Specific rows for its
  // untouched sibling categories (e.g. picking Potato under Starchy leaves a
  // Non-Specific row on Starchy Branded). So a section renders ONE opt-out row
  // only when it has no specific selections at all; otherwise the Non-Specific
  // artifacts are dropped.
  const isOptOut = (pref: RawFoodCategoryPreference) =>
    pref.sub_category === NON_SPECIFIC_SUB_CATEGORY;
  const optOutItem = (label: string): PreferenceItemV2 => ({
    name: `Doesn't eat any ${label.toLowerCase()}`,
    preferred: false,
    optOut: true,
  });

  const masterGroups = MASTER_FOOD_GROUPS.map(({ label, sources, categoryIds }) => {
    const categoryRows = usableCategoryPrefs.filter((pref) =>
      categoryIds.includes(pref.category)
    );
    const specificItems = [
      ...foodPrefs
        .filter((item) => sources.includes(item.input_source))
        .map((item) => ({ name: item.food_name, preferred: item.preferred })),
      ...categoryRows
        .filter((pref) => !isOptOut(pref))
        .map((pref) => ({
          name: pref.sub_category_name,
          preferred: pref.preferred,
        })),
    ];
    const optedOut = specificItems.length === 0 && categoryRows.some(isOptOut);
    return {
      label,
      items: likedFirst(
        optedOut ? [optOutItem(label)] : specificItems
      ),
    };
  });

  // Category ids outside the questionnaire's taxonomy still render, grouped by
  // their backend category name, so no saved answer silently disappears.
  const fallback = new Map<string, PreferenceItemV2[]>();
  usableCategoryPrefs
    .filter((pref) => !mappedCategoryIds.has(pref.category))
    .forEach((pref) => {
      const label = pref.category_name || "Other";
      const items = fallback.get(label) ?? [];
      if (!isOptOut(pref)) {
        items.push({ name: pref.sub_category_name, preferred: pref.preferred });
      } else if (items.length === 0) {
        items.push(optOutItem(label));
      }
      fallback.set(label, items);
    });
  // Same rule as the masters: specifics win over a Non-Specific artifact.
  fallback.forEach((items, label) => {
    if (items.length > 1 && items.some((item) => item.optOut)) {
      fallback.set(
        label,
        items.filter((item) => !item.optOut)
      );
    }
  });

  return [
    ...masterGroups,
    ...Array.from(fallback, ([label, items]) => ({
      label,
      items: likedFirst(items),
    })),
  ].filter((group) => group.items.length > 0);
};

const foodPreferencesSummary = (groups: FoodPreferenceGroupV2[]) => {
  const items = groups.flatMap((group) => group.items);
  if (items.length === 0) return EMPTY;
  const optedOut = items.filter((item) => item.optOut).length;
  const scored = items.filter((item) => !item.optOut);
  const preferred = scored.filter((item) => item.preferred).length;
  const excluded = scored.length - preferred;
  const summary = `${preferred} preferred, ${excluded} excluded`;
  return optedOut > 0 ? `${summary}, ${optedOut} opted out` : summary;
};

// "Meal it replaces": mirrors the mobile v2 view — "No" when the customer has
// no workout meals for the slot or the slot type is None (0).
const workoutReplacementLabel = (meals: string[], type: number | undefined) =>
  meals.length === 0 || !type ? "No" : mealLabel(type);

const workoutSpecificMealsLabel = (pre: string[], post: string[]) => {
  if (pre.length > 0 && post.length > 0) return "Yes — pre & post-workout meals";
  if (pre.length > 0) return "Yes — pre-workout meals only";
  if (post.length > 0) return "Yes — post-workout meals only";
  return "No";
};

export const normalizeNutritionV2 = (
  response: RawNutritionQuestionnaireV2,
  ingredientNames: string[]
): NormalizedNutritionV2 => {
  const foodPrefs = response.food_preferences ?? [];
  const recipePrefs = response.recipe_preferences ?? [];
  const supplements = (response.supplement_preferences ?? [])
    .map((item) => item.supplement_name)
    .filter(Boolean);
  const cheatFoods = response.cheat_foods ?? [];

  // Workout slots (types 8/10) are shown on the Workout Fuels tab, not in the
  // everyday meal list. Sorted by meal-type id (= chronological day order,
  // Breakfast → … → Dinner Snack), regardless of selection order.
  const dailyMealItems = (response.daily_meal_types ?? [])
    .filter((type) => type !== 8 && type !== 10)
    .sort((a, b) => a - b)
    .map((type) => MEAL_TYPE_LABELS[type])
    .filter(Boolean);

  const isFasting =
    (response.intermittent_fasting_start_time ?? "") !== "" &&
    (response.intermittent_fasting_end_time ?? "") !== "";

  const groups = combinedFoodPreferenceGroups(
    foodPrefs,
    response.food_category_preferences ?? []
  );

  const recipePreferences: PreferenceItemV2[] = likedFirst(
    recipePrefs
      .filter((recipe) => recipe.input_source !== INPUT_SOURCE.COFFEE)
      .map((recipe) => ({
        name: recipe.recipe_name,
        preferred: recipe.preferred,
      }))
  );

  const coffeeRecipe = recipePrefs.find(
    (recipe) => recipe.input_source === INPUT_SOURCE.COFFEE
  )?.recipe_name;

  const preWorkoutMeals = namesBySource(foodPrefs, INPUT_SOURCE.PRE_WORKOUT);
  const postWorkoutMeals = namesBySource(foodPrefs, INPUT_SOURCE.POST_WORKOUT);

  return {
    eatingPattern: {
      dietType:
        response.diet_type !== undefined
          ? DIET_TYPE_LABELS[response.diet_type] ?? EMPTY
          : EMPTY,
      dailyMeals: {
        count: `${dailyMealItems.length} meals`,
        items: dailyMealItems,
      },
      intermittentFasting: isFasting ? "Yes" : "No",
      eatsFrom: isFasting
        ? timeOrDash(response.intermittent_fasting_start_time)
        : EMPTY,
      eatsUntil: isFasting
        ? timeOrDash(response.intermittent_fasting_end_time)
        : EMPTY,
      largestMeal: mealLabel(response.biggest_meal_type),
      bedTime: timeOrDash(response.bed_time),
      wakeUpTime: timeOrDash(response.waking_time),
      lunchTime: timeOrDash(response.lunch_time),
    },
    foodPreferences: {
      dietaryRestrictions: decodeDietaryRestrictions(response.allergies ?? 0),
      foodPreferences: {
        summary: foodPreferencesSummary(groups),
        groups,
      },
      keyIngredients: ingredientNames,
      recipePreferences,
    },
    eatingHabits: {
      cheatMeals: cheatFoods.length > 0 ? "Yes" : "No",
      cheatDays: cheatDaysLabel(cheatFoods),
      favouriteCheatMeals: cheatFoods.map((food) => food.food_name),
      alcoholIntake: frequencyLabel(response.alcohol_frequency),
      preferredDrinks: namesBySource(foodPrefs, INPUT_SOURCE.ALCOHOL),
      coffeeIntake: frequencyLabel(response.coffee_frequency),
      coffeeRecipe: coffeeRecipe ?? EMPTY,
    },
    workoutFuels: {
      workoutSpecificMeals: workoutSpecificMealsLabel(
        preWorkoutMeals,
        postWorkoutMeals
      ),
      preWorkoutMeals,
      preWorkoutReplaces: workoutReplacementLabel(
        preWorkoutMeals,
        response.pre_workout_meal_type
      ),
      postWorkoutMeals,
      postWorkoutReplaces: workoutReplacementLabel(
        postWorkoutMeals,
        response.post_workout_meal_type
      ),
      takesSupplements: supplements.length > 0 ? "Yes" : "No",
      supplements,
    },
  };
};
